<script lang="ts">
  /**
   * Le tiroir de jet.
   *
   * L'ADR-017 en fait « l'objet central de l'interface » : il se glisse
   * **par-dessus** la fiche au lieu de la remplacer, si bien qu'on garde ses
   * vitaux et ses chiffres sous les yeux pendant qu'on prépare le Test. On
   * l'ouvre depuis la liste des Compétences, et il reste ouvert quand on change
   * d'onglet — le temps de vérifier une blessure ou un objet avant de lancer.
   *
   * Trois familles de modificateurs, et elles ne se manipulent pas pareil : ce
   * que la fiche impose est coché et verrouillé, ce que le joueur déclare est
   * proposé, ce que le meneur arbitre se saisit.
   */
  import { journaliser } from '../donnees/jets'
  import {
    assistant as calculerAssistant,
    compositionsChanceuses,
    d100 as lancerD100,
    resoudre,
    opposer,
    adversaires,
    competencesAdverses,
    type Demande,
    type Famille,
    type Fiche,
    type Opposition,
    type PersonnageEnregistre,
    type Resolution,
  } from '../rules'

  let {
    vue,
    personnage,
    personnageId,
    competence,
    confrontation,
    declarerConfrontation,
    typeDeTest,
    declarerType,
    depenserChance,
    apresJet,
    fermer,
    decalage = 0,
  }: {
    vue: Fiche
    personnage: PersonnageEnregistre
    personnageId: string
    competence: string
    /** La hauteur de la barre d'onglets : le tiroir se pose dessus, pas
        dessus-dessous — on doit pouvoir changer d'écran sans le refermer. */
    decalage?: number
    /** Déclaré par le joueur : la fiche ne sait pas si on se bat. */
    confrontation: boolean
    declarerConfrontation: (valeur: boolean) => void
    /** L'usage qu'on fait de la Compétence — attaquer, parer, marchander. */
    typeDeTest: string | null
    declarerType: (valeur: string | null) => void
    /** Le Jet chanceux coûte 1 Chance, et c'est la fiche qui la décompte. */
    depenserChance: () => void
    /** Prévient l'écran qu'il y a un jet de plus au journal. */
    apresJet: () => void
    fermer: () => void
  } = $props()

  /** La partie du corps visée. `null` : on frappe où ça tombe. */
  let visee = $state<string | null>(null)
  /** L'arme en main. `null` : à mains nues, ou sans objet. */
  let arme = $state<string | null>(null)

  const cadre = $derived({
    competence,
    confrontation,
    type_de_test: typeDeTest,
    arme,
    visee,
  })
  const panneau = $derived(calculerAssistant(personnage, cadre))
  const armeChoisie = $derived(panneau.armes.find((a) => a.id === arme) ?? null)
  const usage = $derived(panneau.types.find((t) => t.id === panneau.type_retenu) ?? null)

  /* Deux ensembles plutôt qu'un : ce que la fiche impose se recalcule à
     chaque changement — un Statut posé, la Confrontation déclarée —, tandis
     que ce que le joueur a coché lui appartient et ne bouge pas sous ses
     doigts. Le Test retient l'union des deux. */
  const imposes = $derived(
    new Set(panneau.candidats.filter((c) => c.actif).map((c) => c.id)),
  )
  let coches = $state(new Set<string>())
  const retenus = $derived(new Set([...imposes, ...coches]))
  let libre = $state(0)
  let resultat = $state<Resolution | null>(null)
  /* La demande telle qu'elle a été lancée : cocher une case après coup ne doit
     pas réécrire le jet qu'on vient de faire. C'est elle que l'opposition
     rejoue. */
  let lancee = $state<Demande | null>(null)

  /* ── Le camp d'en face ──────────────────────────────────────────────────
     Le livre laisse au meneur le choix de la Compétence opposante (Codex II
     p.33) : on la lui demande, on ne la devine pas. Le bestiaire fournit la
     Valeur quand la créature y figure, et le meneur la saisit sinon. */
  const profils = adversaires()
  let profil = $state<string | null>(null)
  let competenceAdverse = $state<string | null>(null)
  let valeurAdverse = $state(30)
  let modAdverse = $state(0)
  let opposition = $state<Opposition | null>(null)
  const savoirs = $derived(profil ? competencesAdverses(profil) : [])
  const valeurRetenue = $derived(
    savoirs.find((c) => c.id === competenceAdverse)?.valeur ?? valeurAdverse,
  )
  /** Les profils du bestiaire, par section, comme le livre les range. */
  const parSection = $derived(
    [...new Set(profils.map((p) => p.section))].map((section) => ({
      section,
      profils: profils.filter((p) => p.section === section),
    })),
  )

  function opposerLeJet() {
    if (!lancee) return
    opposition = opposer(personnage, lancee, {
      profil,
      competence: competenceAdverse,
      valeur: profil ? null : valeurAdverse,
      modificateur: modAdverse,
      d100: lancerD100(),
    })
  }

  /** Le Jet chanceux : annoncé avant, composé après. */
  let chanceux = $state(false)
  let desTires = $state<[number, number] | null>(null)
  const chanceRestante = $derived(vue.compteurs.chance.valeur)

  const TITRES: Record<Famille, string> = {
    fiche: 'Ce que la fiche impose',
    declare: 'À déclarer',
    mj: 'Arbitré par le meneur',
  }

  const groupe = (f: Famille) => panneau.candidats.filter((c) => c.famille === f)

  /** Le total tel qu'il est coché à l'instant, visée et arbitrage compris. */
  const total = $derived(
    panneau.candidats
      .filter((c) => retenus.has(c.id))
      .reduce((somme, c) => somme + c.valeur, 0) +
      (panneau.visees.find((v) => v.localisation === visee)?.valeur ?? 0) +
      libre,
  )

  function basculer(id: string) {
    const suivant = new Set(coches)
    if (suivant.has(id)) suivant.delete(id)
    else suivant.add(id)
    coches = suivant
  }

  /**
   * Lance le ou les dés.
   *
   * Un Jet chanceux n'en finit pas là : le livre fait lancer deux D100, puis
   * laisse le joueur composer son résultat. On s'arrête donc au tirage, et on
   * attend qu'il choisisse.
   */
  function jeter() {
    if (chanceux) {
      desTires = [lancerD100(), lancerD100()]
      resultat = null
      return
    }
    void conclure(lancerD100())
  }

  async function conclure(d100: number, des?: number[]) {
    const demande = { cadre, retenus: [...retenus], libre, d100 }
    const r = resoudre(personnage, demande)
    resultat = r
    lancee = demande
    // Un nouveau jet du joueur périme l'issue précédente : l'adversaire
    // relancera le sien.
    opposition = null
    if (des) depenserChance()
    await journaliser(personnageId, competence, panneau.nom, r, des)
    apresJet()
  }

  function composer(valeur: number) {
    if (!desTires) return
    const des = [...desTires]
    desTires = null
    chanceux = false
    void conclure(valeur, des)
  }

  const VERDICTS: Record<string, string> = {
    reussite_critique: 'Réussite critique',
    reussite: 'Réussite',
    echec: 'Échec',
    echec_critique: 'Échec critique',
  }

  /* Le vrai signe moins, et non le trait d'union : c'est un nombre, pas une
     césure. */
  const signe = (v: number) => (v < 0 ? `−${Math.abs(v)}` : `+${v}`)
  /** Le même soin pour un total, qui n'a pas à porter de signe quand il est
      positif : une Difficulté négative, elle, reste lisible. */
  const nombre = (v: number) => (v < 0 ? `−${Math.abs(v)}` : `${v}`)
</script>

<div
  class="tiroir"
  role="dialog"
  aria-label="Assistant de jet — {panneau.nom}"
  style:bottom="{decalage}px"
>
  <div class="poignee">
    <div class="quoi">
      <span class="nom">{panneau.nom}</span>
      <span class="calcul data">
        {panneau.reelle}
        {total === 0 ? '' : total < 0 ? '−' : '+'}{total === 0 ? '' : Math.abs(total)}
        {#if total !== 0}<b>= {nombre(panneau.reelle + total)}</b>{/if}
      </span>
    </div>
    <button type="button" class="fermer" onclick={fermer} aria-label="Fermer le tiroir">✕</button>
  </div>

  <div class="dedans">
    {#if desTires}
      <div class="groupe">
        <h3>Composez votre résultat</h3>
        <p class="aide">
          Les dés donnent <strong class="data">{desTires[0]}</strong> et
          <strong class="data">{desTires[1]}</strong>. Prenez la dizaine de l'un et
          l'unité de l'autre — le plus bas passe le mieux, mais un doublé fait un
          Critique.
        </p>
        <div class="compositions">
          {#each compositionsChanceuses(desTires[0], desTires[1]) as valeur (valeur)}
            <button type="button" class="jet" onclick={() => composer(valeur)}>{valeur}</button>
          {/each}
        </div>
      </div>
    {:else if resultat}
      <div
        class="resultat"
        class:est-ok={resultat.reussite && !resultat.critique}
        class:est-ko={!resultat.reussite && !resultat.critique}
        class:est-crit={resultat.critique}
      >
        <span class="de data">{resultat.d100}</span>
        <div class="dit">
          <span class="verdict">{VERDICTS[resultat.verdict]}</span>
        <span class="detail">
          {resultat.d100} contre {resultat.difficulte}
          {#if resultat.epique}, difficulté épique — la réussite est acquise{/if}
          · {Math.abs(resultat.degres)} degré{Math.abs(resultat.degres) > 1 ? 's' : ''}
          {resultat.degres < 0 ? "d'échec" : 'de réussite'}
        </span>
        {#if resultat.degats}
          <p class="degats">
            <strong class="data">{resultat.degats.total}</strong>
            Dégâts
            <span class="detail">
              {resultat.degats.arme} {resultat.degats.base}
              {resultat.degats.degres < 0 ? '−' : '+'}{Math.abs(resultat.degats.degres)}
              degré{Math.abs(resultat.degats.degres) > 1 ? 's' : ''}
              {resultat.degats.degres < 0 ? "d'échec" : 'de réussite'}
            </span>
            <!-- Ce que le Veredium monté sur l'arme ajoute. Un terme assorti
                 d'une clause n'entre pas dans le total : c'est au meneur de
                 dire s'il s'applique. -->
            {#each resultat.degats.termes as t (t.origine)}
              <span class="detail">
                {signe(t.valeur)} {t.origine}
                {#if t.condition}<span class="alerte">{t.condition} — à trancher</span>{/if}
              </span>
            {/each}
          </p>
        {/if}
          <ul class="pile">
            <li><span>Valeur réelle</span> <span class="data">{resultat.reelle}</span></li>
            {#each resultat.termes as t, i (t.origine + i)}
              <li><span>{t.origine}</span> <span class="data">{signe(t.valeur)}</span></li>
            {/each}
          </ul>
        </div>
      </div>
    {/if}

    <!-- Un Test opposé ne se lit pas seul : le livre ne compare que les Degrés,
         indépendamment de la réussite de chacun (Codex II p.33-34). -->
    {#if usage?.oppose}
      <div class="groupe en-face">
        <h3>En face</h3>
        <p class="aide">{usage.oppose}</p>

        <div class="rangee">
          <select class="saisie" bind:value={profil} onchange={() => (competenceAdverse = null)}>
            <option value={null}>Adversaire improvisé</option>
            {#each parSection as g (g.section)}
              <optgroup label={g.section}>
                {#each g.profils as a (a.id)}
                  <option value={a.id}>{a.nom}{a.elite ? ' (Élite)' : ''}</option>
                {/each}
              </optgroup>
            {/each}
          </select>

          {#if profil}
            <select class="saisie" bind:value={competenceAdverse}>
              <option value={null}>Quelle Compétence ?</option>
              {#each savoirs as c (c.id)}
                <option value={c.id}>{c.nom} — {c.valeur}</option>
              {/each}
            </select>
          {:else}
            <label class="champ">
              Valeur
              <input class="saisie" type="number" min="1" max="120" bind:value={valeurAdverse} />
            </label>
          {/if}

          <label class="champ">
            Modificateur
            <input class="saisie" type="number" min="-60" max="60" bind:value={modAdverse} />
          </label>

          <button
            type="button"
            class="jet"
            disabled={!lancee || (profil !== null && competenceAdverse === null)}
            onclick={opposerLeJet}
          >
            Lancer pour lui <span class="data">{valeurRetenue + modAdverse}</span>
          </button>
        </div>

        {#if !lancee}
          <p class="aide">Lancez d'abord votre propre dé : c'est lui qu'on oppose.</p>
        {/if}

        {#if opposition}
          <div
            class="issue"
            class:est-ok={opposition.vainqueur === 'joueur'}
            class:est-ko={opposition.vainqueur === 'adversaire'}
          >
            <span class="de data">{opposition.adverse.d100}</span>
            <div class="dit">
              <span class="verdict">
                {opposition.vainqueur === 'joueur'
                  ? "L'action passe"
                  : `${opposition.adverse.nom} l'emporte`}
              </span>
              <span class="detail">{opposition.raison}</span>
              <span class="detail">
                {opposition.adverse.nom}{#if opposition.adverse.competence}, {opposition.adverse
                    .competence}{/if}
                : {opposition.adverse.d100} contre {opposition.adverse.difficulte} ·
                {VERDICTS[opposition.adverse.verdict]} ·
                {signe(opposition.adverse.degres)} DR contre {signe(opposition.joueur.degres)}
              </span>
            </div>
          </div>
        {/if}
      </div>
    {/if}

    <!-- Déclaré et non deviné : une part du catalogue ne vaut que pendant une
         Confrontation, et la fiche ne sait pas si son porteur se bat. -->
    <div class="contexte">
      <button
        type="button"
        class="pilule"
        aria-pressed={!confrontation}
        onclick={() => declarerConfrontation(false)}
      >
        Hors Confrontation
      </button>
      <button
        type="button"
        class="pilule"
        aria-pressed={confrontation}
        onclick={() => declarerConfrontation(true)}
      >
        En Confrontation
      </button>
    </div>

    {#if panneau.types.length > 0}
      <div class="groupe">
        <h3>Ce qu'on en fait</h3>
        <div class="rangee">
          {#each panneau.types as t (t.id)}
            <button
              type="button"
              class="pilule"
              title={t.resume}
              aria-pressed={panneau.type_retenu === t.id}
              onclick={() => declarerType(panneau.type_retenu === t.id ? null : t.id)}
            >
              {t.nom}
            </button>
          {/each}
        </div>
        {#if usage?.oppose}
          <p class="aide">{usage.oppose}</p>
        {/if}
      </div>
    {/if}

    {#if panneau.armes.length > 0}
      <div class="groupe">
        <h3>Arme en main</h3>
        <div class="rangee">
          <button
            type="button"
            class="pilule"
            aria-pressed={arme === null}
            onclick={() => (arme = null)}
          >
            Aucune
          </button>
          {#each panneau.armes as a (a.id)}
            <button
              type="button"
              class="pilule"
              aria-pressed={arme === a.id}
              onclick={() => (arme = arme === a.id ? null : a.id)}
            >
              {a.nom}
              {#if a.degats !== null}<span class="data">{a.degats} dég.</span>{/if}
              {#if a.maitrise_requise && a.maitrise === 0}
                <span class="alerte">sans Maîtrise</span>
              {:else if a.maitrise > 1}
                <span class="data">Maîtrise {a.maitrise}</span>
              {/if}
            </button>
          {/each}
        </div>
        {#if armeChoisie && armeChoisie.proprietes.length > 0}
          <p class="aide">
            {armeChoisie.categorie_nom} · {armeChoisie.proprietes.join(', ')}
          </p>
        {/if}
      </div>
    {/if}

    {#each ['fiche', 'declare', 'mj'] as const as f}
      {#if groupe(f).length > 0}
        <div class="groupe">
          <h3>{TITRES[f]}</h3>
          {#each groupe(f) as c (c.id)}
            <label class="candidat" class:verrouille={f === 'fiche'}>
              <input
                type="checkbox"
                checked={retenus.has(c.id)}
                disabled={f === 'fiche'}
                onchange={() => basculer(c.id)}
              />
              <span class="origine">
                {c.origine}
                {#if c.condition}<span class="condition">{c.condition}</span>{/if}
              </span>
              <span class="valeur data">{signe(c.valeur)}</span>
            </label>
          {/each}
        </div>
      {/if}
    {/each}

    {#if panneau.visees.length > 0}
      <div class="groupe">
        <h3>Visée — au prix d'une Demi-action</h3>
        <div class="rangee serree">
          <button
            type="button"
            class="pilule"
            aria-pressed={visee === null}
            onclick={() => (visee = null)}
          >
            Aucune
          </button>
          {#each panneau.visees as v (v.localisation)}
            <button
              type="button"
              class="pilule"
              aria-pressed={visee === v.localisation}
              onclick={() => (visee = visee === v.localisation ? null : v.localisation)}
            >
              {v.nom} <span class="data">{signe(v.valeur)}</span>
            </button>
          {/each}
        </div>
      </div>
    {/if}

    <div class="groupe">
      <h3>Chance</h3>
      <label class="candidat" class:verrouille={chanceRestante === 0}>
        <input
          type="checkbox"
          checked={chanceux}
          disabled={chanceRestante === 0}
          onchange={() => (chanceux = !chanceux)}
        />
        <span class="origine">
          Jet chanceux — deux D100, la dizaine de l'un et l'unité de l'autre
          {#if chanceRestante === 0}
            <span class="condition">Plus de Chance : impossible avant le prochain repos.</span>
          {/if}
        </span>
        <span class="valeur data">{chanceRestante} / {vue.compteurs.chance.max}</span>
      </label>
    </div>

    <div class="groupe">
      <h3>Arbitrage</h3>
      <label class="candidat">
        <span class="origine">Couvert, luminosité — ce que le livre chiffre ailleurs</span>
        <input
          class="saisie libre data"
          type="text"
          inputmode="numeric"
          value={libre}
          aria-label="Modificateur libre"
          oninput={(e) => (libre = Number.parseInt(e.currentTarget.value, 10) || 0)}
        />
      </label>
    </div>
  </div>

  <div class="pied">
    <button type="button" class="jet grand" onclick={jeter}>
      {chanceux ? 'Lancer deux D100' : 'Lancer le D100'}
    </button>
  </div>
</div>

<style>
  /* Le tiroir monte du bas et laisse la fiche visible au-dessus : c'est tout
     l'intérêt: on garde ses vitaux sous les yeux pendant qu'on prépare le jet. */
  .tiroir {
    position: fixed;
    left: 0;
    right: 0;
    bottom: 0;
    z-index: 6;
    display: flex;
    flex-direction: column;
    max-height: 74svh;
    background: var(--surface);
    border-top: 2px solid var(--gold);
    border-radius: 12px 12px 0 0;
    box-shadow: 0 -16px 44px rgba(0, 0, 0, 0.42);
    animation: monte 0.18s ease-out;
  }
  @keyframes monte {
    from {
      transform: translateY(14px);
      opacity: 0.4;
    }
  }
  /* Sur large écran, le tiroir se range à droite : la fiche reste entièrement
     lisible à côté, plutôt qu'à moitié cachée dessous. */
  @media (min-width: 900px) {
    .tiroir {
      left: auto;
      right: calc(var(--pas) * 4);
      width: 30rem;
      max-height: 80svh;
      border-radius: 12px 12px 0 0;
    }
  }

  .poignee {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: calc(var(--pas) * 2);
    padding: calc(var(--pas) * 2.5) calc(var(--pas) * 4);
    border-bottom: 1px solid var(--line);
    background: var(--surface-2);
    border-radius: 10px 10px 0 0;
  }
  .quoi {
    display: flex;
    align-items: baseline;
    gap: calc(var(--pas) * 3);
    min-width: 0;
  }
  .nom {
    font-family: 'Cinzel', serif;
    text-transform: uppercase;
    letter-spacing: 0.08em;
    font-size: 0.82rem;
    color: var(--gold);
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }
  .calcul {
    color: var(--ink-soft);
    font-size: 0.9rem;
  }
  .calcul b {
    color: var(--ink);
    font-size: 1.15rem;
  }
  .fermer {
    flex: none;
    width: 32px;
    height: 32px;
    border: 1px solid var(--line);
    border-radius: var(--r);
    background: transparent;
    color: var(--ink-soft);
    cursor: pointer;
    font-size: 0.85rem;
  }
  .fermer:hover {
    border-color: var(--line-fort);
    color: var(--ink);
  }

  /* Une pile verticale, et non une grille : la rangée d'une grille mesure mal
     un enfant qui est lui-même une boîte flexible, et le bloc de résultat s'y
     retrouvait haut de cinquante pixels — sa pile de modificateurs se
     dessinait alors par-dessus les sections suivantes. Empilés, les blocs
     gardent leur hauteur et c'est le tiroir qui défile. */
  .dedans {
    overflow-y: auto;
    padding: calc(var(--pas) * 3) calc(var(--pas) * 4);
    display: flex;
    flex-direction: column;
    gap: calc(var(--pas) * 3);
  }
  .dedans > * {
    flex: none;
  }
  .pied {
    padding: calc(var(--pas) * 2) calc(var(--pas) * 4);
    padding-bottom: max(calc(var(--pas) * 2), env(safe-area-inset-bottom));
    border-top: 1px solid var(--line);
    background: var(--surface-2);
  }
  .pied .jet {
    width: 100%;
  }

  .contexte,
  .rangee {
    display: flex;
    flex-wrap: wrap;
    gap: var(--pas);
  }
  .rangee.serree .pilule {
    font-size: 0.65rem;
  }
  /* Une arme qu'on ne maîtrise pas reste utilisable : le livre pénalise, il
     n'interdit pas. La pastille le dit sans crier. */
  .alerte {
    color: var(--ko);
    font-size: 0.6rem;
    text-transform: none;
    letter-spacing: 0;
  }

  .groupe h3 {
    font-family: 'Cinzel', serif;
    font-size: 0.65rem;
    text-transform: uppercase;
    letter-spacing: 0.1em;
    color: var(--ink-soft);
    margin: 0 0 var(--pas);
  }
  .aide {
    color: var(--ink-soft);
    font-size: 0.82rem;
    margin: 0 0 calc(var(--pas) * 2);
  }
  /* L'issue de l'opposition se lit comme le résultat du joueur, en plus
     sobre : c'est une conséquence, pas l'événement. */
  .issue {
    display: flex;
    align-items: flex-start;
    gap: var(--pas);
    padding: var(--pas);
    border-radius: 3px;
    border: 1px solid var(--trait);
  }
  .issue .dit {
    flex: 1;
    min-width: 0;
    display: grid;
    gap: 2px;
  }
  .issue.est-ok {
    border-color: var(--ok);
  }
  .issue.est-ko {
    border-color: var(--ko);
  }
  .en-face .rangee {
    align-items: flex-end;
  }
  .en-face select.saisie {
    max-width: 20ch;
  }
  .champ {
    display: grid;
    gap: 2px;
    font-size: 0.68rem;
    font-family: var(--titre);
    letter-spacing: 0.06em;
    text-transform: uppercase;
    color: var(--pale);
  }
  .champ input {
    width: 7ch;
  }
  .compositions {
    display: flex;
    gap: calc(var(--pas) * 2);
    flex-wrap: wrap;
  }
  .compositions .jet {
    font-size: 1.1rem;
    min-width: 4.5rem;
  }

  .candidat {
    display: grid;
    grid-template-columns: auto 1fr auto;
    align-items: center;
    gap: calc(var(--pas) * 2);
    padding: calc(var(--pas) * 1.5) 0;
    border-bottom: 1px solid var(--line);
    font-size: 0.9rem;
    cursor: pointer;
  }
  .candidat.verrouille {
    cursor: default;
  }
  .candidat:last-child {
    border-bottom: none;
  }
  .origine {
    display: grid;
    gap: 2px;
  }
  .condition {
    color: var(--ink-soft);
    font-size: 0.75rem;
  }
  .valeur {
    font-size: 1rem;
    font-weight: 600;
  }
  .libre {
    width: 5rem;
    text-align: center;
  }

  /* Le verdict, en tête du tiroir : c'est ce qu'on vient y chercher. */
  .resultat {
    display: flex;
    align-items: flex-start;
    gap: calc(var(--pas) * 3);
    padding: calc(var(--pas) * 3);
    border: 1px solid var(--line-fort);
    border-radius: var(--r);
    background: var(--surface-2);
  }
  /* Le dé en gouttière, tout ce qu'il entraîne dans une colonne à côté : la
     grille précédente replaçait ses lignes dès qu'il s'en ajoutait une, et le
     détail passait sous le chiffre. */
  .dit {
    flex: 1;
    min-width: 0;
    display: grid;
    gap: 2px;
  }
  .de {
    flex: none;
    font-size: 2.4rem;
    font-weight: 700;
    line-height: 1;
  }
  .verdict {
    font-family: 'Cinzel', serif;
    text-transform: uppercase;
    letter-spacing: 0.08em;
    font-size: 0.9rem;
  }
  .detail {
    color: var(--ink-soft);
    font-size: 0.78rem;
  }
  .est-ok .verdict,
  .est-ok .de {
    color: var(--ok);
  }
  .est-ko .verdict,
  .est-ko .de {
    color: var(--ko);
  }
  .est-crit .verdict,
  .est-crit .de {
    color: var(--gold);
  }
  /* Ce que le coup inflige : le second chiffre qu'on annonce à table, juste
     après le verdict, et celui que l'autre joueur va devoir encaisser. */
  .degats {
    margin: calc(var(--pas) * 2) 0 0;
    font-family: 'Cinzel', serif;
    text-transform: uppercase;
    letter-spacing: 0.06em;
    font-size: 0.7rem;
  }
  .degats .data {
    font-family: system-ui, -apple-system, 'Segoe UI', sans-serif;
    font-size: 1.5rem;
    color: var(--ko);
    margin-right: var(--pas);
  }
  .degats .detail {
    display: block;
    text-transform: none;
    letter-spacing: 0;
  }

  .pile {
    list-style: none;
    margin: calc(var(--pas) * 2) 0 0;
    padding: calc(var(--pas) * 2) 0 0;
    border-top: 1px solid var(--line);
    display: grid;
    gap: 2px;
    font-size: 0.78rem;
    color: var(--ink-soft);
  }
  .pile li {
    display: flex;
    justify-content: space-between;
  }
</style>
